import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  Layers,
  Database,
  Cpu,
  Server,
  ChevronsRight,
  ArrowLeft,
  ArrowRight,
  Grid,
  List,
  CheckCircle,
  Clock,
  Zap,
  X,
  Building,
  Settings,
  Shield,
  Users,
  Calendar,
  HelpCircle,
} from 'lucide-react';
import { championIdeaArchitecture } from '../../data/championIdeaArchitecture';
import { ZachmanLayer, ZachmanComponent } from '../../types';

interface ArchitectureLayersProps {
  isEmbedded?: boolean;
}

const getLayerIcon = (perspective: string) => {
  switch (perspective) {
    case 'business':
      return Building;
    case 'application':
      return Settings;
    case 'data':
      return Database;
    case 'technology':
      return Cpu;
    case 'infrastructure':
      return Server;
    default:
      return Layers;
  }
};

const ArchitectureLayers: React.FC<ArchitectureLayersProps> = ({ isEmbedded = false }) => {
  const [view, setView] = useState<'layers' | 'matrix'>('layers');
  const [selectedLayer, setSelectedLayer] = useState<(ZachmanLayer & { icon: React.ElementType }) | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<ZachmanComponent | null>(null);
  const [modalCell, setModalCell] = useState<{ layer: ZachmanLayer & { icon: React.ElementType }; interrogative: string } | null>(null);

  const architectureData: (ZachmanLayer & { icon: React.ElementType })[] = useMemo(() => {
    return championIdeaArchitecture.map(layer => ({
      ...layer,
      icon: getLayerIcon(layer.perspective),
    }));
  }, []);

  const handleLayerClick = (layer: ZachmanLayer & { icon: React.ElementType }) => {
    setSelectedLayer(layer);
    setSelectedComponent(null); // Reset component selection when layer changes
  };

  const handleComponentClick = (component: ZachmanComponent) => {
    setSelectedComponent(component);
  };
  
  const handleBackToLayers = () => {
    setSelectedLayer(null);
    setSelectedComponent(null);
  };

  const handleBackToComponents = () => {
    setSelectedComponent(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in-progress':
        return 'text-blue-600 bg-blue-100';
      case 'planned':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in-progress':
        return <Clock className="w-4 h-4" />;
      case 'planned':
        return <Calendar className="w-4 h-4" />;
      default:
        return <HelpCircle className="w-4 h-4" />;
    }
  };

  const renderLayersList = () => (
    <div className="space-y-3">
      {architectureData.map((layer) => (
        <div
          key={layer.id}
          className={`p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer flex items-center space-x-4 ${
            selectedLayer?.id === layer.id
              ? 'bg-white border-green-700 shadow-lg'
              : 'bg-white/60 border-slate-300 hover:border-green-600 hover:shadow-md'
          }`}
          onClick={() => handleLayerClick(layer)}
        >
          <div className="flex-shrink-0">
            <div
              className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                selectedLayer?.id === layer.id ? 'sqsft-gradient-primary' : 'bg-gray-700'
              }`}
            >
              <layer.icon className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="flex-grow">
            <h3 className="text-xl font-bold text-slate-800">{layer.name}</h3>
            <p className="text-slate-500 text-base mt-1">{layer.components.length} components</p>
          </div>
          <div className="flex-shrink-0">
            <ArrowRight className="w-6 h-6 text-slate-400" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderLayerDetail = () => {
    if (!selectedLayer) return null;

    return (
      <AnimatePresence mode="wait">
        {!selectedComponent ? (
          <motion.div
            key="component-list"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center mb-6">
              <button onClick={handleBackToLayers} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
                <ArrowLeft className="w-6 h-6 text-slate-600" />
              </button>
              <div className="ml-4 flex items-center">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center sqsft-gradient-primary`}>
                  <selectedLayer.icon className="w-7 h-7 text-white" />
                </div>
                <div className="ml-4">
                  <h2 className="text-3xl font-bold text-slate-800">{selectedLayer.name}</h2>
                  <p className="text-slate-500">{selectedLayer.description}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedLayer.components.map(component => (
                <div
                  key={component.id}
                  onClick={() => handleComponentClick(component)}
                  className="bg-white p-5 rounded-xl border border-slate-200 hover:border-green-500 hover:shadow-lg transition-all cursor-pointer"
                >
                  <h4 className="text-lg font-bold text-slate-800 truncate">{component.name}</h4>
                  <p className="text-sm text-slate-500 h-10 overflow-hidden">{component.description}</p>
                  <div className={`mt-3 inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(component.status)}`}>
                    {getStatusIcon(component.status)}
                    <span className="capitalize">{component.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="component-detail"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="p-1"
          >
            <div className="flex items-center mb-6">
              <button onClick={handleBackToComponents} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
                <ArrowLeft className="w-6 h-6 text-slate-600" />
              </button>
               <div className="ml-4">
                <h2 className="text-3xl font-bold text-slate-800">{selectedComponent.name}</h2>
                <p className="text-slate-500">{selectedComponent.description}</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <div className={`mb-4 inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedComponent.status)}`}>
                  {getStatusIcon(selectedComponent.status)}
                  <span className="capitalize">{selectedComponent.status}</span>
                </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">WHAT (Data)</h5>
                  <p className="text-slate-800">{selectedComponent.what}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">HOW (Function)</h5>
                  <p className="text-slate-800">{selectedComponent.how}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">WHERE (Network)</h5>
                  <p className="text-slate-800">{selectedComponent.where}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">WHO (People)</h5>
                  <p className="text-slate-800">{selectedComponent.who}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">WHEN (Time)</h5>
                  <p className="text-slate-800">{selectedComponent.when}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h5 className="font-semibold text-slate-500 text-sm">WHY (Motivation)</h5>
                  <p className="text-slate-800">{selectedComponent.why}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const interrogatives = ['what', 'how', 'where', 'who', 'when', 'why'];

  const renderMatrixView = () => (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="p-3 text-left text-sm font-semibold text-slate-600 w-1/4 sticky left-0 bg-slate-100 z-10">Layer</th>
              {interrogatives.map(q => (
                <th key={q} className="p-3 text-center text-sm font-semibold text-slate-600 capitalize">{q}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {architectureData.map((layer, layerIndex) => (
              <tr key={layer.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold text-slate-700 sticky left-0 bg-white z-10">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center sqsft-gradient-primary`}>
                      <layer.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-base">{layer.name.split('(')[0].trim()}</span>
                  </div>
                </td>
                {interrogatives.map(q => {
                  const component = layer.components.find(c => c.name.toLowerCase().startsWith(q));
                  return (
                    <td
                      key={`${layer.id}-${q}`}
                      className="p-3 text-sm text-slate-600 align-top border-l border-slate-200 transition-colors hover:bg-green-50 cursor-pointer"
                      onClick={() => component && setModalCell({ layer, interrogative: q })}
                    >
                      {component ? component[q as 'what' | 'how' | 'where' | 'who' | 'when' | 'why'] : 'N/A'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderModal = () => {
    if (!modalCell) return null;
    const { layer, interrogative } = modalCell;
    const component = layer.components.find(c => c.name.toLowerCase().startsWith(interrogative));
    if (!component) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setModalCell(null)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
               <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">{layer.name}</h3>
                    <p className="text-lg text-green-600 font-semibold capitalize">{component.name}</p>
                  </div>
                  <button onClick={() => setModalCell(null)} className="p-2 -mr-2 -mt-2 rounded-full hover:bg-slate-100 transition-colors">
                    <X className="w-6 h-6 text-slate-500" />
                  </button>
               </div>
               <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div >
                    <h5 className="font-semibold text-slate-500 text-sm">WHAT</h5>
                    <p className="text-slate-800">{component.what}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-500 text-sm">HOW</h5>
                    <p className="text-slate-800">{component.how}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-500 text-sm">WHERE</h5>
                    <p className="text-slate-800">{component.where}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-500 text-sm">WHO</h5>
                    <p className="text-slate-800">{component.who}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-500 text-sm">WHEN</h5>
                    <p className="text-slate-800">{component.when}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-500 text-sm">WHY</h5>
                    <p className="text-slate-800">{component.why}</p>
                  </div>
               </div>
            </div>
            <div className={`p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end space-x-2 ${getStatusColor(component.status)}`}>
              {getStatusIcon(component.status)}
              <span className="text-sm font-medium capitalize">{`Status: ${component.status}`}</span>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className={`min-h-screen ${isEmbedded ? 'p-0' : 'sqsft-gradient-secondary p-4 sm:p-6 lg:p-8'}`}>
      <div className={`container mx-auto ${isEmbedded ? '' : 'max-w-7xl'}`}>
        {!isEmbedded && (
          <header className="mb-6 text-center">
            <h1 className="text-4xl font-extrabold text-gray-800">Architecture Layers</h1>
            <p className="mt-2 text-base text-gray-600">An interactive view of the Homomorphic ML Platform using the Zachman Framework.</p>
          </header>
        )}

        <div className="bg-white/50 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              {selectedLayer && (
                <AnimatePresence>
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onClick={handleBackToLayers}
                    className="flex items-center space-x-2 text-slate-600 font-semibold hover:text-green-700 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span>All Layers</span>
                  </motion.button>
                </AnimatePresence>
              )}
            </div>
            
            <div className="flex items-center bg-slate-200 rounded-lg p-1 space-x-1">
              <button
                onClick={() => setView('layers')}
                className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
                  view === 'layers' ? 'bg-white text-green-700 shadow' : 'text-slate-600 hover:bg-slate-300/50'
                }`}
              >
                <List className="w-5 h-5 inline-block mr-1" />
                Layers
              </button>
              <button
                onClick={() => setView('matrix')}
                className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
                  view === 'matrix' ? 'bg-white text-green-700 shadow' : 'text-slate-600 hover:bg-slate-300/50'
                }`}
              >
                <Grid className="w-5 h-5 inline-block mr-1" />
                Matrix
              </button>
            </div>
          </div>
          
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {view === 'layers' ? (
                <div className="relative">
                  <AnimatePresence mode="wait">
                    {!selectedLayer ? (
                      <motion.div
                        key="layers-list"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, position: 'absolute', width: '100%' }}
                        transition={{ duration: 0.3 }}
                      >
                        {renderLayersList()}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="layer-detail"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, position: 'absolute', width: '100%' }}
                        transition={{ duration: 0.3 }}
                      >
                        {renderLayerDetail()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                renderMatrixView()
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      {renderModal()}
    </div>
  );
};

export default ArchitectureLayers;